import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import StarterTemplatesPaneController, {
    StarterTemplatesPaneControllerState,
} from "js/controllers/StarterTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { trackActivity } from "js/core/utilities/utilities";
import StarterTemplateList from "js/editor/CreatePresentation/starterTemplates/StarterTemplateList";
import getObjectHash from "common/utils/getObjectHash";
import { LibraryTemplateGenerateOptions } from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";
import { Button } from "js/Components/Button";
import { t } from "js/i18n";
import { BetaBadge } from "js/react/components/BetaBadge";

const TAG_FILTER_ALL = "__all__";

const TemplatesContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    flex: 1;
    gap: 20px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
`;

const GenerateButton = styled(Button)`
    &&& > span {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
    }
`;

const GenerateBetaBadge = styled(BetaBadge).attrs({
    as: "span",
    small: true,
})`
    display: flex;
    padding: 2px 4px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.50);
    font-size: 10px;
`;

export interface ChooseStarterTemplatePaneProps {
    tagFilter?: string;
    onCategoryClick?: (category: string) => void;
    onCreate: (options: { type: "starter-template"; templateId: string }) => void;
    onGenerate?: (options: LibraryTemplateGenerateOptions) => void;
}

type ChooseStarterTemplatePaneComponentProps =
    WorkspaceControllerState &
    StarterTemplatesPaneControllerState &
    ChooseStarterTemplatePaneProps;

function ChooseStarterTemplatePaneInner({
    tagFilter: externalTagFilter,
    onCategoryClick,
    onCreate,
    onGenerate,
    initialized,
    filters,
    data
}: ChooseStarterTemplatePaneComponentProps) {
    const selectedTag = externalTagFilter ?? TAG_FILTER_ALL;
    const [selectedRecord, setSelectedRecord] = useState<StarterTemplatesPaneControllerState["data"][number] | null>(null);

    const controllerFilters = (() => {
        if (selectedTag === TAG_FILTER_ALL) {
            return [];
        }

        return [{
            field: "resource.tags",
            operator: "eq" as const,
            value: selectedTag,
        }];
    })();

    useEffect(() => {
        StarterTemplatesPaneController.inititialize({
            sort: [{
                field: "resource.createdAt",
                direction: "desc" as const,
            }],
            filters: controllerFilters,
        });

        return () => {
            StarterTemplatesPaneController.reset();
        };
    }, []);

    useEffect(() => {
        if (!initialized) {
            return;
        }

        if (getObjectHash(controllerFilters) === getObjectHash(filters)) {
            return;
        }

        StarterTemplatesPaneController.setFilters(controllerFilters);
    }, [getObjectHash(controllerFilters), getObjectHash(filters), initialized]);

    useEffect(() => {
        setSelectedRecord(current => (
            current && data.some(record => record.document.id === current.document.id) ? current : null
        ));
    }, [getObjectHash(data)]);

    const handleCreate = useCallback((templateId: string) => {
        onCreate({ type: "starter-template", templateId });
        trackActivity("Presentation", "Start", null, null, {
            source: "starter-template",
            template_id: templateId,
        }, { audit: false });
    }, [onCreate]);

    const handleGenerate = useCallback(() => {
        if (!selectedRecord || !onGenerate) {
            return;
        }

        const { document } = selectedRecord;

        trackActivity("Presentation", "Start", null, null, {
            source: "starter-template",
            creation_method: "generate-with-ai",
            template_id: document.id,
            template_name: document.name,
        }, { audit: false });

        onGenerate({
            selectedTemplate: {
                id: document.id,
                name: document.name,
            },
        });
    }, [onGenerate, selectedRecord]);

    return (
        <TemplatesContainer>
            <ResultsContainer>
                <StarterTemplateList
                    selectedId={selectedRecord?.document.id ?? null}
                    onSelect={setSelectedRecord}
                    onCreate={handleCreate}
                    onCategoryClick={onCategoryClick}
                />
            </ResultsContainer>
            <Actions>
                <Button
                    large
                    width={120}
                    blue
                    disabled={!selectedRecord}
                    onClick={() => selectedRecord && handleCreate(selectedRecord.document.id)}
                >
                    {t("Create")}
                </Button>
                {onGenerate && (
                    <GenerateButton
                        large
                        ai
                        disabled={!selectedRecord}
                        onClick={handleGenerate}
                    >
                        {t("Generate")}
                        <GenerateBetaBadge>
                            {t("Beta")}
                        </GenerateBetaBadge>
                    </GenerateButton>
                )}
            </Actions>
        </TemplatesContainer>
    );
}

export const ChooseStarterTemplatePane = WorkspaceController.withInitializedState(
    StarterTemplatesPaneController.withState(ChooseStarterTemplatePaneInner)
);
